import React, { useState } from "react";
import { toast } from "sonner";
import { ClipboardList, LogIn, Plus, Smartphone } from "lucide-react";

import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { MAX_BLOCKS, STEPS } from "../blocks";

import type { LucideIcon } from "lucide-react";
import type { OnboardingBlock } from "@/shared/types";

const menuCls = "w-[300px] p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu";

/* The "+" between two steps of the canvas, on the line that joins them: it opens the steps
   that can go there. Questions any number of times; a sign-in once ("Already in the flow"
   while there is one); the phone step is coming soon, so it shows but adds nothing. A full
   flow says so instead of opening. */
export function AddStep({ hasLogin, full, onAdd }: {
  hasLogin: boolean;
  full: boolean;
  onAdd: (type: OnboardingBlock["type"]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ways: { type: OnboardingBlock["type"]; Icon: LucideIcon; label: string; hint: string; off: boolean }[] = [
    { type: "questions", Icon: ClipboardList, label: "Questions", hint: "Ask for details, answered together on one screen", off: false },
    { type: "login", Icon: LogIn, label: STEPS.login.name, hint: hasLogin ? "Already in the flow" : STEPS.login.add, off: hasLogin },
    { type: "phone", Icon: Smartphone, label: STEPS.phone.name, hint: STEPS.phone.add, off: true },
  ];
  return (
    <div className="flex flex-col items-center">
      <span aria-hidden="true" className="w-px h-2.5 bg-[var(--hairline-strong)]" />
      <DropdownMenu
        open={open}
        onOpenChange={(next) => {
          if (next && full) toast.error(`A flow holds at most ${MAX_BLOCKS} steps`);
          else setOpen(next);
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Add a step here"
            title="Add a step here"
            className="press w-6 h-6 rounded-[7px] flex items-center justify-center bg-[var(--surface)] text-[var(--text-secondary)] ring-1 ring-[var(--hairline-strong)] hover:bg-[var(--text-primary)] hover:text-[var(--surface)] data-[state=open]:bg-[var(--text-primary)] data-[state=open]:text-[var(--surface)]"
          >
            <Plus className="w-3 h-3" strokeWidth={2.25} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className={menuCls}>
          <DropdownMenuLabel className="px-2 pt-1.5 pb-1 text-[11.5px] font-medium text-[var(--text-tertiary)]">Add a step</DropdownMenuLabel>
          {ways.map(({ type, Icon, label, hint, off }) => (
            <DropdownMenuItem
              key={type}
              disabled={off}
              onSelect={() => onAdd(type)}
              className="flex items-start gap-2.5 px-2 py-[7px] rounded-[8px] cursor-pointer focus:bg-[var(--accent-hover)]"
            >
              <span className="w-7 h-7 shrink-0 rounded-[8px] bg-[var(--surface-2)] flex items-center justify-center text-[var(--text-secondary)]">
                <Icon className="w-[14px] h-[14px]" strokeWidth={1.75} />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-medium text-[var(--text-primary)]">{label}</span>
                <span className="block text-[12px] leading-[1.45] text-[var(--text-tertiary)]">{hint}</span>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <span aria-hidden="true" className="w-px h-2.5 bg-[var(--hairline-strong)]" />
    </div>
  );
}
